import { createClient } from '@supabase/supabase-js';
import type { Database } from '@/types/database';

// VaulTex Supabase client.
// Only the public anon key is used in the browser; RLS enforces access
// (Constitución: Seguridad por diseño + Menor privilegio). The service-role
// key never reaches the frontend — it lives in Edge Functions / CI secrets.
const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/** True cuando la app corre contra el backend real; con false los servicios
 *  usan fixtures (Fase A) y ningún entorno se rompe por falta de config. */
export const hasBackend = Boolean(url && anonKey);

if (!hasBackend) {
  // Never surface a raw technical error to the user (Constitución §15);
  // this guard helps developers during setup only.
  console.warn('[VaulTex] Supabase env vars missing. Copy .env.example → .env.local.');
}

// "Recuérdame" (VLT-386): la sesión vive en localStorage (sobrevive cerrar el
// navegador) SOLO si el usuario lo pidió; sin marcar, va a sessionStorage y
// muere al cerrar. El flag se fija ANTES del login; el adaptador lo lee en
// cada escritura. Lecturas miran ambos; borrar limpia ambos.
const REMEMBER_FLAG = 'vtx-remember';
export const setRememberMe = (v: boolean) => {
  try { localStorage.setItem(REMEMBER_FLAG, v ? '1' : '0'); } catch { /* sin storage */ }
};
const remembering = () => {
  try { return localStorage.getItem(REMEMBER_FLAG) !== '0'; } catch { return true; }
};
const authStorage = {
  getItem: (k: string) => {
    try { return sessionStorage.getItem(k) ?? localStorage.getItem(k); } catch { return null; }
  },
  setItem: (k: string, v: string) => {
    try { (remembering() ? localStorage : sessionStorage).setItem(k, v); } catch { /* sin storage */ }
  },
  removeItem: (k: string) => {
    try { sessionStorage.removeItem(k); localStorage.removeItem(k); } catch { /* sin storage */ }
  },
};

// En modo demo (sin variables) NO se crea el cliente — createClient lanza con
// URL vacía. Todos los servicios verifican `hasBackend` antes de tocar
// `supabase`, así que en demo este valor jamás se dereferencia.
export const supabase = (hasBackend
  ? createClient<Database>(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true,
        storage: authStorage,
      },
      db: { schema: 'vaultex' },
      global: {
        headers: { 'x-vaultex-client': 'web' },
      },
    })
  : null) as ReturnType<typeof createClient<Database>>;
